import { useMemo, useState } from "react";
import { TrendingUp } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorCard } from "@/components/shared/ErrorCard";
import { EmptyState } from "@/components/shared/EmptyState";
import { useDashboardRevenue, type RevenueRange } from "@/hooks/useDashboard";
import { formatCurrency } from "@/utils/format";

const RANGES: Array<{ value: RevenueRange; label: string }> = [
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "12m", label: "12m" },
];

const compactCurrency = (value: number): string => {
  if (Math.abs(value) >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (Math.abs(value) >= 1000) return `₹${(value / 1000).toFixed(1)}k`;
  return `₹${value}`;
};

const formatTick = (date: string, granularity: "day" | "month"): string => {
  if (granularity === "month") {
    const [, mm] = date.split("-");
    const month = new Date(Number(date.split("-")[0]), Number(mm) - 1, 1);
    return month.toLocaleString("en-GB", { month: "short" });
  }
  const d = new Date(date);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short" });
};

export const RevenueChart = () => {
  const [range, setRange] = useState<RevenueRange>("30d");
  const { data, isLoading, error, refetch } = useDashboardRevenue(range);

  const buckets = useMemo(() => data?.buckets ?? [], [data?.buckets]);
  const granularity = data?.granularity ?? "day";

  const totals = useMemo(() => {
    const revenue = buckets.reduce((acc, b) => acc + b.revenue, 0);
    const bookings = buckets.reduce((acc, b) => acc + b.bookings, 0);
    return { revenue, bookings };
  }, [buckets]);

  const hasAny = buckets.some((b) => b.revenue > 0 || b.bookings > 0);

  return (
    <div className="bg-card rounded-card border border-border p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-foreground">Revenue over time</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading
              ? "Loading…"
              : `${formatCurrency(totals.revenue)} across ${totals.bookings} bookings`}
          </p>
        </div>
        <div className="inline-flex rounded-btn border border-border bg-muted/50 p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRange(r.value)}
              className={`px-3 py-1 text-xs font-medium rounded-btn transition-colors ${
                range === r.value
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-64 rounded-card" />
      ) : error ? (
        <ErrorCard message={(error as Error).message} onRetry={refetch} />
      ) : !hasAny ? (
        <EmptyState
          icon={TrendingUp}
          title="No revenue yet"
          description={`No paid payments in the last ${range === "12m" ? "12 months" : range === "90d" ? "90 days" : "30 days"}.`}
        />
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={buckets}
              margin={{ top: 10, right: 8, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor="hsl(var(--primary))"
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="100%"
                    stopColor="hsl(var(--primary))"
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                stroke="hsl(var(--border))"
                strokeDasharray="3 3"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => formatTick(d, granularity)}
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false}
                axisLine={false}
                minTickGap={granularity === "month" ? 0 : 24}
              />
              <YAxis
                tickFormatter={compactCurrency}
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false}
                axisLine={false}
                width={48}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelFormatter={(d) => formatTick(String(d), granularity)}
                formatter={(value, key) => {
                  const v = Number(value ?? 0);
                  return key === "revenue"
                    ? [formatCurrency(v), "Revenue"]
                    : [String(v), "Bookings"];
                }}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fill="url(#revenueFill)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default RevenueChart;
